import { Finding, type FindingsRepository } from "@telespot/domain";
import { deadlined, logged, retryable } from "@shared/decorators";
import { ParseBaseRepository } from "../../parse-base.repository";
import { FindingTopology, ParseFindingMapper } from "./parse-findings.mapper";
import { LatestVersionTopology } from "../latest-version.topology";
import { ObjectTopology } from "../../parse.topology";
import { AnalysisTopology } from "./parse-analysis.mapper";

export class ParseFindingRepository extends ParseBaseRepository implements FindingsRepository {

  private readonly findingMapper = new ParseFindingMapper(this.parse);

  @logged()
  @deadlined()
  public async save(finding: Finding): Promise<string> {
    const parseFinding = this.findingMapper.fromDomain(finding);

    const { id } = await parseFinding.save(null, this.options);

    return id;
  }

  @logged()
  @deadlined()
  public async saveAll(...findings: Finding[]): Promise<string[]> {
    const parseObjects = findings.map((f) => this.findingMapper.fromDomain(f));

    const results = await this.parse.Object.saveAll(parseObjects, this.options);

    return results.map((r) => r.id);
  }

  @retryable()
  @logged()
  @deadlined()
  public async getByUUID(uuid: string): Promise<Finding | undefined> {
    const parseFinding = await new this.parse.Query(FindingTopology.TABLE)
      .equalTo(FindingTopology.UUID, uuid)
      .first(this.options);

    return parseFinding ? this.findingMapper.toDomain(parseFinding) : undefined;
  }

  @retryable()
  @logged()
  @deadlined()
  public async getById(id: string): Promise<Finding | undefined> {
    const parseFinding = await new this.parse.Query(FindingTopology.TABLE).get(
      id,
      this.options
    );

    return parseFinding ? this.findingMapper.toDomain(parseFinding) : undefined;
  }

  @retryable()
  @logged()
  @deadlined()
  public async getLatestVersion(uuid: string): Promise<Finding | undefined> {
    const parseLatestVersionQuery = new this.parse.Query(
      LatestVersionTopology.TABLE
    )
      .equalTo(LatestVersionTopology.ENTITY, FindingTopology.TABLE)
      .equalTo(LatestVersionTopology.ENTITY_ID, uuid);

    const parseLatestFindingQuery = new this.parse.Query(FindingTopology.TABLE)
      .matchesKeyInQuery(
        ObjectTopology.ID,
        LatestVersionTopology.VERSION_ID,
        parseLatestVersionQuery
      )
      .include(FindingTopology.ANALYSIS)
      .include(FindingTopology.PIPELINE_STEP);

    const parseLatestFinding = await parseLatestFindingQuery.first(
      this.options
    );

    return parseLatestFinding
      ? this.findingMapper.toDomain(parseLatestFinding)
      : undefined;
  }

  @retryable()
  @logged()
  @deadlined()
  public async getVersion(uuid: string, version: number): Promise<Finding | undefined> {
    const findingQuery = new this.parse.Query(FindingTopology.TABLE)
      .equalTo(FindingTopology.UUID, uuid)
      .equalTo(FindingTopology.VERSION, version)
      .include(FindingTopology.ANALYSIS)
      .include(FindingTopology.PIPELINE_STEP);

    const findingVersion = await findingQuery.first(this.options);

    return findingVersion
      ? this.findingMapper.toDomain(findingVersion)
      : undefined;
  }

  /**
   * Not marked, though it only reads. A generator returns synchronously and the
   * Retrier replays a rejected promise, so the mark would declare a guarantee the
   * mechanism cannot honour - and a partially drained generator is not replayable
   * from the start anyway. Same for the generators on the analysis repository.
   */
  public async *getAllVersions(uuid: string): AsyncGenerator<Finding[]> {
    const findingQuery = new this.parse.Query(FindingTopology.TABLE)
      .equalTo(FindingTopology.UUID, uuid)
      .descending(FindingTopology.VERSION)
      .include(FindingTopology.ANALYSIS)
      .include(FindingTopology.PIPELINE_STEP);

    const batches = this.getGeneratorFromQuery(findingQuery);

    for await (const items of batches) {
      yield items.map((item) => this.findingMapper.toDomain(item));
    }
  }

  @retryable()
  @logged()
  @deadlined()
  public async getLatestVersionByAnalysis(analysisId: string): Promise<Finding | undefined> {

    const analysisRef = this.subclasses.getSubclass(AnalysisTopology.TABLE).createWithoutData(analysisId);

    const findingQuery = new this.parse.Query(FindingTopology.TABLE)
      .equalTo(FindingTopology.ANALYSIS, analysisRef)
      .descending(FindingTopology.VERSION)
      .include(FindingTopology.ANALYSIS)
      .include(FindingTopology.PIPELINE_STEP);

    const finding = await findingQuery.first(this.options);

    return finding ? this.findingMapper.toDomain(finding) : undefined;
  }
}
